/**
 * Component Name: SummaryStats
 * Description: A component that displays summary statistics and controls for invoice data, including a toggleable chart and grouping options.
 * Props:
 *   - summaryStats (object): Aggregated statistics (total invoices, amount, suppliers, POs, payment status).
 *   - fetchInvoiceData (function): Callback to fetch invoice data.
 *   - handleMultiSelectChange (function): Callback to handle group selection changes.
 *   - selectedGroupOption (object): The currently selected grouping option.
 *   - groupOptions (array): List of available grouping options.
 *   - handleOpenFilterModal (function): Callback to open the filter modal.
 *   - filteredInvoices (array): List of filtered invoice objects.
 *   - projects (array): List of project objects for chart rendering.
 *   - selectedGroup (string): Current grouping type ('project', 'section', 'po', etc.).
 *   - subTable (object, optional): Sub-table filter data for chart rendering.
 *   - onChartBarClick (function): Callback for handling chart bar clicks.
 *   - chartDrillDownState (object, optional): State for chart drill-down.
 * Feature: Displays key invoice statistics, provides buttons for refreshing data, applying filters, and toggling a chart, and includes a dropdown for selecting grouping options in the invoice management interface.
 */
import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import Autorenew from '@mui/icons-material/Autorenew';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import MultiSelect from '../inputs/multi-select';
import InvoiceChart from './InvoiceChart'; // Import the new component

const SummaryStats = ({
    summaryStats,
    fetchInvoiceData,
    handleMultiSelectChange,
    selectedGroupOption,
    groupOptions,
    handleOpenFilterModal,
    filteredInvoices,
    projects,
    selectedGroup,
    subTable, // Pass subTable prop to InvoiceChart
    onChartBarClick, // Pass the new onChartBarClick prop
    chartDrillDownState // Pass chartDrillDownState
}) => {
    const [showChart, setShowChart] = useState(false);

    return (
        <div className="summary-stats fst-normal d-flex flex-wrap align-items-center position-relative gap-2 z-1">
            <div className="d-flex align-items-center gap-1 px-3 py-1 bg-white bg-opacity-10 rounded-5 stat">
                <span>Total Invoices:</span>
                <span className="stat-value fs-6 fw-bold">{summaryStats.totalInvoices}</span>
            </div>
            <div className="d-flex align-items-center gap-1 px-3 py-1 bg-white bg-opacity-10 rounded-5 stat">
                <span>Total Invoice Amount:</span>
                <span className="stat-value fs-6 fw-bold">{summaryStats.totalAmount}</span>
            </div>
            <div className="d-flex align-items-center gap-1 px-3 py-1 bg-white bg-opacity-10 rounded-5 stat">
                <span>Suppliers:</span>
                <span className="stat-value fs-6 fw-bold">{summaryStats.totalSuppliers}</span>
            </div>
            <div className="d-flex align-items-center gap-1 px-3 py-1 bg-white bg-opacity-10 rounded-5 stat">
                <span>Purchase Orders:</span>
                <span className="stat-value fs-6 fw-bold">{summaryStats.totalPOs}</span>
            </div>

            <div>
                <Tooltip title="Refresh Data" arrow={true} placement='top' followCursor={true}>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            fetchInvoiceData();
                        }}
                        aria-label="Refresh"
                        className="btn p-0 px-1"
                    >
                        <Autorenew />
                    </button>
                </Tooltip>
                <Tooltip title="Filters" arrow={true} placement='top' followCursor={true}>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenFilterModal();
                        }}
                        aria-label="Filter"
                        className="btn p-0 px-1"
                    >
                        <FilterAltIcon />
                    </button>
                </Tooltip>
                <Tooltip title={showChart ? "Hide Chart" : "Show Chart"} arrow={true} placement='top' followCursor={true}>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowChart(!showChart);
                        }}
                        aria-label="Toggle Chart"
                        aria-pressed={showChart}
                        className="btn p-0 px-1 text-white"
                    >
                        <BarChartIcon />
                    </button>
                </Tooltip>
            </div>
            <div className="group-select">
                <MultiSelect
                    value={selectedGroupOption}
                    onChangeHandler={handleMultiSelectChange}
                    isMulti={false}
                    placeholder="Select Grouping"
                    items={groupOptions}
                    aria-label="Group by"
                    dropdownWidth="170%"
                    data-cy="group-dropdown"
                />
            </div>

            {showChart && (
                <InvoiceChart
                    filteredInvoices={filteredInvoices}
                    projects={projects}
                    selectedGroup={selectedGroup}
                    subTable={subTable}
                    chartDrillDownState={chartDrillDownState} // Pass chartDrillDownState
                    onChartBarClick={onChartBarClick}
                />
            )}
        </div>
    );
};

export default SummaryStats;